import {
  allTradeScopes,
  GENERAL_QUANTITIES_TRADE,
} from "bundles/projects/consts";

// PSS mode: when enabled, all trade inference returns Mechanical/HVAC.
// Set from ssrProps.pssGdpSpecialEnabled via ProjectTakeoffsContainer's useEffect.
let _pssTradeOverride = false;
export const setPssTradeOverride = (enabled: boolean): void => {
  _pssTradeOverride = enabled;
};

// Mapping of page code prefixes to trade types
export const TRADE_TYPE_MAPPING: Record<string, string> = {
  G: "General",
  A: "Architectural",
  S: "Structural",
  C: "Civil",
  L: "Landscape",
  M: "Mechanical (HVAC)",
  P: "Plumbing",
  FP: "Fire Protection",
  E: "Electrical",
  T: "Low Voltage / Technology",
  LV: "Low Voltage / Technology",
  LS: "Life Safety / Code",
  U: "Utilities",
  I: "Interiors",
  F: "Food Service",
  X: "Specialty / Process",
  FA: "Fire Alarm",
  SS: "Security Systems",
  AV: "Audio Visual",
  IT: "Information Technology",
  MG: "Medical Gas",
  EQ: "Equipment / Process",
  EN: "Energy Systems",
  R: "Roadway / Paving",
  W: "Water / Storm / Drainage",
  DS: "Drainage / Stormwater",
  SAN: "Sanitary Sewer",
  V: "Vertical Transportation (Elevators)",
  FFE: "Furniture, Fixtures & Equipment",
  D: "Demolition",
  N: "Survey / Existing Conditions",
};

/**
 * Infers trade type from page code prefix
 * Examples: "A-101" -> "Architectural", "FP-1" -> "Fire Protection"
 * Returns "unnamed" if no match found
 */
export const inferTradeFromPageCode = (pageCode: string): string => {
  if (_pssTradeOverride) return "Mechanical (HVAC)";
  if (!pageCode) return "unnamed";

  // Try three-character prefixes first (FFE, SAN, etc.)
  const threeCharPrefix = pageCode.substring(0, 3).toUpperCase();
  if (TRADE_TYPE_MAPPING[threeCharPrefix]) {
    return TRADE_TYPE_MAPPING[threeCharPrefix];
  }

  // Try two-character prefixes (FP, LV, LS, etc.)
  const twoCharPrefix = pageCode.substring(0, 2).toUpperCase();
  if (TRADE_TYPE_MAPPING[twoCharPrefix]) {
    return TRADE_TYPE_MAPPING[twoCharPrefix];
  }

  // Try single character prefix
  const oneCharPrefix = pageCode.substring(0, 1).toUpperCase();
  if (TRADE_TYPE_MAPPING[oneCharPrefix]) {
    return TRADE_TYPE_MAPPING[oneCharPrefix];
  }

  // No match found
  return "unnamed";
};

/**
 * Infers a valid trade key from page code prefix by matching the display label
 * against allTradeScopes keys. Returns empty string if no match found.
 *
 * Unlike inferTradeFromPageCode (which returns display labels like "Mechanical (HVAC)"),
 * this returns valid trade keys like "mechanical/hvac" suitable for API calls and DB storage.
 *
 * Examples: "M-101" -> "mechanical/hvac", "S-201" -> "construction/structural", "XYZ" -> ""
 */
export const inferTradeKeyFromPageCode = (pageCode: string): string => {
  if (_pssTradeOverride) return "mechanical/hvac";
  const displayLabel = inferTradeFromPageCode(pageCode);
  if (displayLabel === "unnamed") return "";

  const tradeWords = displayLabel.toLowerCase().split(/[\s_/-]+/);
  // Exclude general-quantities — "G" page codes mean general notes/site plans, not the GQ trade
  const availableTrades = Object.keys(allTradeScopes).filter(
    (t) => t !== GENERAL_QUANTITIES_TRADE,
  );

  for (const trade of availableTrades) {
    const tradeKey = trade.toLowerCase();
    if (
      tradeWords.some(
        (word) => tradeKey.includes(word) || word.includes(tradeKey),
      )
    ) {
      return trade;
    }
  }

  return "";
};

type TradeScopePair = { trade: string; scope: string };

export type ResolveAllowedTradesInput = {
  subscriptionAllowedTrades?: string[] | null;
  aiHandlers: TradeScopePair[] | null;
};

/**
 * Single source of truth for the trade dropdown's enabled list.
 *
 * `subscriptionAllowedTrades` arrives already resolved by the backend
 * `Subscription::Quota::TradeLimitResolver`, which incorporates the flag,
 * ATS override, current cycle snapshot, and live tier in one pass.
 *
 * Tri-state contract:
 *   subscriptionAllowedTrades === null/undefined → deny all (backend signalled no source)
 *   subscriptionAllowedTrades === []             → allow all aiHandler trades
 *   subscriptionAllowedTrades === [trades]       → aiHandlers ∩ subscriptionAllowedTrades
 *
 * Returns `null` while aiHandlers is still loading so callers can treat
 * that as "not yet known".
 */
export const resolveAllowedTrades = ({
  subscriptionAllowedTrades,
  aiHandlers,
}: ResolveAllowedTradesInput): string[] | null => {
  if (aiHandlers == null) return null;
  const aiTrades = Array.from(new Set(aiHandlers.map((h) => h.trade)));

  if (subscriptionAllowedTrades == null) return [];
  if (subscriptionAllowedTrades.length === 0) return aiTrades;

  const limitSet = new Set(subscriptionAllowedTrades);
  return aiTrades.filter((t) => limitSet.has(t));
};

export type ResolveAutoSelectTradeInput = {
  subscriptionAllowedTrades?: string[] | null;
  aiHandlers?: TradeScopePair[] | null;
  pageCode: string;
};

/**
 * Resolves which trade to auto-select for a page.
 *
 *   subscriptionAllowedTrades === null/undefined → "" (deny all — nothing valid to pick)
 *   subscriptionAllowedTrades === []             → inferred (allow all; intersect with
 *                                   aiHandlers if available)
 *   subscriptionAllowedTrades === [trades]       → inferred if in subscriptionAllowedTrades (intersect
 *                                   with aiHandlers if available); else ""
 *
 * When `aiHandlers` is omitted/null we skip the aiHandler intersection so
 * first-load auto-select works before the health-check fetch completes.
 * The dropdown corrects the value once the resolved set arrives.
 */
export const resolveAutoSelectTrade = ({
  subscriptionAllowedTrades,
  aiHandlers,
  pageCode,
}: ResolveAutoSelectTradeInput): string => {
  const inferred = inferTradeKeyFromPageCode(pageCode);
  if (!inferred) return "";
  if (subscriptionAllowedTrades == null) return "";

  if (subscriptionAllowedTrades.length > 0 && !subscriptionAllowedTrades.includes(inferred)) return "";

  if (aiHandlers != null) {
    const aiTrades = new Set(aiHandlers.map((h) => h.trade));
    if (!aiTrades.has(inferred)) return "";
  }

  return inferred;
};

/**
 * Extracts unique trades from a list of page codes
 * Returns sorted array of trade types
 */
export const extractUniqueTradesFromPageCodes = (
  pageCodes: string[],
): string[] => {
  const trades = new Set<string>();

  pageCodes.forEach((pageCode) => {
    const trade = inferTradeFromPageCode(pageCode);
    trades.add(trade);
  });

  // Convert to array and sort alphabetically
  return Array.from(trades).sort();
};
